import { Head, router } from "@inertiajs/react"
import React, { useState, useEffect } from "react"
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts"
import DashboardLayout from "@/components/dashboard/layout"
import IndexToolbar from "@/components/dashboard/index-toolbar"
import Pagination from "@/components/pagination"
import { Button } from "@/components/ui/button"
import { PLATFORM_LABELS } from "@/lib/social-post-constants"

type AggregateMetrics = {
  views: number
  likes: number
  comments: number
  shares: number
  reach: number
  saves: number
  engagement_rate: number
  post_count: number
}

type PlatformBreakdown = {
  platform: string
  views: number
  likes: number
  comments: number
  shares: number
  post_count: number
}

type PostPerformance = {
  id: number
  display_name: string
  platform: string
  post_kind: string
  platform_post_url: string | null
  published_at: string | null
  views: number
  likes: number
  comments: number
  shares: number
  reach: number
  saves: number
  engagement_rate: number
}

type TimeSeriesPoint = {
  date: string
  views: number
  likes: number
  comments: number
  shares: number
  posts: number
}

type Filters = {
  days: number
  platform: string | null
  post_kind: string | null
  sort: string
  direction: string
}

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

const PLATFORM_COLOURS: Record<string, string> = {
  instagram: "#E1306C",
  facebook: "#1877F2",
  tiktok: "#000000",
  youtube: "#FF0000",
}

const ENGAGEMENT_COLOURS = {
  views: { stroke: "#C41E2A", label: "Views" },
  likes: { stroke: "#D4A853", label: "Likes" },
  comments: { stroke: "#3A3A3C", label: "Comments" },
  shares: { stroke: "#8E8E93", label: "Shares" },
} as const

type ChartTab = "engagement" | "platform"

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

function timeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

const ANALYTICS_FILTERS = [
  {
    label: "All platforms",
    param: "platform",
    options: [
      { value: "instagram", label: "Instagram" },
      { value: "facebook", label: "Facebook" },
      { value: "tiktok", label: "TikTok" },
      { value: "youtube", label: "YouTube" },
    ],
  },
  {
    label: "All types",
    param: "post_kind",
    options: [
      { value: "video", label: "Video" },
      { value: "listing", label: "Listing" },
      { value: "standalone", label: "Post" },
    ],
  },
  {
    label: "30 days",
    param: "days",
    options: [
      { value: "7", label: "7 days" },
      { value: "30", label: "30 days" },
      { value: "90", label: "90 days" },
    ],
  },
]

const ANALYTICS_SORT_OPTIONS = [
  { value: "published_at", label: "Date published" },
  { value: "views", label: "Views" },
  { value: "likes", label: "Likes" },
  { value: "comments", label: "Comments" },
  { value: "shares", label: "Shares" },
]

const TABLE_COLUMNS = [
  { key: "platform", label: "Platform", sortable: false },
  { key: "post", label: "Post", sortable: false },
  { key: "published_at", label: "Date", sortable: true },
  { key: "views", label: "Views", sortable: true, align: "right" as const },
  { key: "likes", label: "Likes", sortable: true, align: "right" as const },
  { key: "comments", label: "Comments", sortable: true, align: "right" as const },
  { key: "shares", label: "Shares", sortable: true, align: "right" as const },
]

export default function AnalyticsIndex({
  aggregate_metrics,
  platform_breakdown,
  post_performance,
  time_series,
  filters,
  pagination,
  last_refreshed_at,
}: {
  aggregate_metrics: AggregateMetrics
  platform_breakdown: PlatformBreakdown[]
  post_performance: PostPerformance[]
  time_series: TimeSeriesPoint[]
  filters: Filters
  pagination: PaginationMeta
  last_refreshed_at: string | null
}) {
  const [refreshStartedAt, setRefreshStartedAt] = useState<string | null>(null)
  const [chartTab, setChartTab] = useState<ChartTab>("engagement")

  const refreshing = refreshStartedAt !== null &&
    (last_refreshed_at === null || last_refreshed_at === refreshStartedAt)

  // Poll every 3s while refreshing, clean up when done or after 60s
  useEffect(() => {
    if (!refreshing) return

    const interval = setInterval(() => {
      router.reload()
    }, 3000)
    const timeout = setTimeout(() => setRefreshStartedAt(null), 60000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [refreshing])

  const currentSort = filters.sort || "published_at"
  const currentDirection = filters.direction || "desc"

  // Build current filter params for toolbar and pagination
  const currentFilters: Record<string, string> = {}
  if (filters.platform) currentFilters.platform = filters.platform
  if (filters.post_kind) currentFilters.post_kind = filters.post_kind
  if (filters.days && filters.days !== 30) currentFilters.days = String(filters.days)
  if (currentSort !== "published_at") currentFilters.sort = currentSort
  if (currentDirection !== "desc") currentFilters.direction = currentDirection

  function handleSort(column: string) {
    const newDirection = currentSort === column && currentDirection === "asc" ? "desc" : "asc"
    const params: Record<string, string> = { ...currentFilters, sort: column, direction: newDirection }
    delete params.page
    router.get("/dashboard/analytics", params, { preserveState: true, preserveScroll: true })
  }

  function handleRefresh() {
    setRefreshStartedAt(last_refreshed_at ?? "none")
    router.post("/dashboard/analytics/refresh", {}, { preserveScroll: true })
  }

  const hasData = aggregate_metrics.post_count > 0

  return (
    <DashboardLayout>
      <Head title="Analytics | QuiteRight" />

      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
            Analytics
          </h1>
          <p className="mt-1 text-warm-grey">
            Track engagement across your social posts.
            {last_refreshed_at && (
              <span className="ml-2 text-xs text-warm-grey/70">
                Last updated {timeAgo(last_refreshed_at)}
              </span>
            )}
          </p>
        </div>
        <Button size="sm" className="bg-red text-white hover:bg-deep-red" onClick={handleRefresh} disabled={refreshing}>
          <svg className={`mr-1.5 size-3.5 ${refreshing ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
          </svg>
          {refreshing ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      {!hasData ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone/60 bg-white px-8 py-16 text-center">
          <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-stone/20">
            <svg className="size-8 text-warm-grey" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
            </svg>
          </div>
          <h2 className="mb-2 font-display text-xl font-semibold text-charcoal">
            No published posts yet
          </h2>
          <p className="max-w-sm text-warm-grey">
            Publish posts to social media to see analytics here.
          </p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetricCard label="Posts" value={formatCompact(aggregate_metrics.post_count)} />
            <MetricCard label="Views" value={formatCompact(aggregate_metrics.views)} />
            <MetricCard label="Likes" value={formatCompact(aggregate_metrics.likes)} />
            <MetricCard label="Shares" value={formatCompact(aggregate_metrics.shares)} />
            <MetricCard label="Comments" value={formatCompact(aggregate_metrics.comments)} />
            <MetricCard label="Engagement rate" value={`${aggregate_metrics.engagement_rate}%`} />
          </div>

          {/* Tabbed chart card */}
          <div className="mb-6 rounded-xl border border-stone/30 bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setChartTab("engagement")}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    chartTab === "engagement"
                      ? "bg-red text-white"
                      : "text-warm-grey hover:bg-stone/20 hover:text-charcoal"
                  }`}
                >
                  Engagement
                </button>
                <button
                  type="button"
                  onClick={() => setChartTab("platform")}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    chartTab === "platform"
                      ? "bg-red text-white"
                      : "text-warm-grey hover:bg-stone/20 hover:text-charcoal"
                  }`}
                >
                  By platform
                </button>
              </div>

              {chartTab === "engagement" && (
                <div className="flex gap-4">
                  {(Object.entries(ENGAGEMENT_COLOURS) as [string, { stroke: string; label: string }][]).map(
                    ([key, { stroke, label }]) => (
                      <div key={key} className="flex items-center gap-1.5">
                        <span className="size-2 rounded-full" style={{ backgroundColor: stroke }} />
                        <span className="text-[11px] text-warm-grey">{label}</span>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>

            {chartTab === "engagement" ? (
              <EngagementChart data={time_series} />
            ) : (
              <PlatformChart data={platform_breakdown} />
            )}
          </div>

          {/* Post performance */}
          <IndexToolbar
            filters={ANALYTICS_FILTERS}
            sortOptions={ANALYTICS_SORT_OPTIONS}
            currentFilters={currentFilters}
            currentSort={currentSort}
            currentDirection={currentDirection}
            baseUrl="/dashboard/analytics"
            count={pagination.count}
            noun="post"
          />

          {post_performance.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-stone/15">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-stone/15 bg-stone/5 text-left">
                    {TABLE_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-warm-grey ${col.align === "right" ? "text-right" : ""}`}
                      >
                        {col.sortable ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 transition-colors hover:text-charcoal"
                            onClick={() => handleSort(col.key)}
                          >
                            {col.label}
                            <SortIcon
                              active={currentSort === col.key}
                              direction={currentSort === col.key ? currentDirection : undefined}
                            />
                          </button>
                        ) : (
                          col.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {post_performance.map((pp) => (
                    <tr key={pp.id} className="border-b border-stone/10 bg-white transition-colors hover:bg-stone/5">
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-2">
                          <PlatformIcon platform={pp.platform} className="size-4 shrink-0" />
                          <span className="text-sm font-medium text-charcoal">
                            {PLATFORM_LABELS[pp.platform]}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-[200px] truncate text-sm">
                          {pp.platform_post_url ? (
                            <a
                              href={pp.platform_post_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-charcoal hover:text-red"
                            >
                              {pp.display_name || "Untitled"}
                            </a>
                          ) : (
                            <span className="font-medium text-charcoal">
                              {pp.display_name || "Untitled"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-warm-grey/60">
                        {pp.published_at
                          ? new Date(pp.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                          : "—"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-warm-grey">{formatCompact(pp.views)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-warm-grey">{formatCompact(pp.likes)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-warm-grey">{formatCompact(pp.comments)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-warm-grey">{formatCompact(pp.shares)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone/60 bg-white px-8 py-12 text-center">
              <p className="text-sm text-warm-grey">No posts match your filters.</p>
            </div>
          )}

          <Pagination
            pagination={pagination}
            baseUrl="/dashboard/analytics"
            params={currentFilters}
          />
        </>
      )}
    </DashboardLayout>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction?: string }) {
  if (!active) {
    return (
      <svg className="size-3 opacity-30" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
      </svg>
    )
  }
  return direction === "asc" ? (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  ) : (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function EngagementChart({ data }: { data: TimeSeriesPoint[] }) {
  if (data.length === 0) {
    return (
      <div className="flex h-60 items-center justify-center text-sm text-warm-grey">
        Not enough data to display a chart.
      </div>
    )
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <defs>
            <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C41E2A" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#C41E2A" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gLikes" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D4A853" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#D4A853" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gComments" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3A3A3C" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#3A3A3C" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gShares" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8E8E93" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#8E8E93" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" />
          <XAxis
            dataKey="date"
            tickFormatter={(d: string) => {
              const date = new Date(d)
              return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
            }}
            tick={{ fontSize: 11, fill: "#8E8E93" }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fontSize: 11, fill: "#8E8E93" }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip content={<EngagementTooltip />} />
          <Area type="monotone" dataKey="views" stroke="#C41E2A" fill="url(#gViews)" strokeWidth={1.5} dot={false} />
          <Area type="monotone" dataKey="likes" stroke="#D4A853" fill="url(#gLikes)" strokeWidth={1.5} dot={false} />
          <Area type="monotone" dataKey="comments" stroke="#3A3A3C" fill="url(#gComments)" strokeWidth={1.5} dot={false} />
          <Area type="monotone" dataKey="shares" stroke="#8E8E93" fill="url(#gShares)" strokeWidth={1.5} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

function EngagementTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  const formattedDate = label
    ? new Date(String(label)).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : ""

  return (
    <div className="rounded-lg border border-stone/30 bg-white px-3 py-2 shadow-md">
      {formattedDate && (
        <p className="mb-1.5 text-[11px] font-medium text-warm-grey">{formattedDate}</p>
      )}
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 text-xs">
          <span className="size-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="capitalize text-warm-grey">{entry.name}</span>
          <span className="ml-auto tabular-nums font-medium text-charcoal">
            {formatCompact(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

function PlatformChart({ data }: { data: PlatformBreakdown[] }) {
  if (data.length === 0) {
    return (
      <div className="flex h-60 items-center justify-center text-sm text-warm-grey">
        No platform data available.
      </div>
    )
  }

  const chartData = data
    .map((pb) => ({
      platform: PLATFORM_LABELS[pb.platform] || pb.platform,
      post_count: pb.post_count,
      views: pb.views,
      colour: PLATFORM_COLOURS[pb.platform] || "#3A3A3C",
    }))
    .sort((a, b) => b.post_count - a.post_count)

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" vertical={false} />
          <XAxis
            dataKey="platform"
            tick={{ fontSize: 11, fill: "#8E8E93" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#8E8E93" }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip
            content={<PlatformTooltip />}
            cursor={{ fill: "rgba(232,228,223,0.3)" }}
          />
          <Bar dataKey="post_count" radius={[4, 4, 0, 0]} barSize={40} name="Posts">
            {chartData.map((entry) => (
              <Cell key={entry.platform} fill={entry.colour} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function PlatformTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{
    payload: { platform: string; post_count: number; views: number; colour: string }
  }>
}) {
  if (!active || !payload?.length) return null

  const d = payload[0].payload
  return (
    <div className="rounded-lg border border-stone/30 bg-white px-3 py-2 shadow-md">
      <p className="mb-1 text-[11px] font-medium text-warm-grey">{d.platform}</p>
      <div className="flex items-center gap-2 text-xs">
        <span className="size-1.5 rounded-full" style={{ backgroundColor: d.colour }} />
        <span className="text-warm-grey">Posts</span>
        <span className="ml-auto tabular-nums font-medium text-charcoal">{d.post_count}</span>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="size-1.5 rounded-full" style={{ backgroundColor: d.colour, opacity: 0.5 }} />
        <span className="text-warm-grey">Views</span>
        <span className="ml-auto tabular-nums font-medium text-charcoal">{formatCompact(d.views)}</span>
      </div>
    </div>
  )
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#E1306C">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  )
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1A1A1A">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  )
}

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#FF0000">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

const PLATFORM_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  tiktok: TikTokIcon,
  youtube: YouTubeIcon,
}

function PlatformIcon({ platform, className }: { platform: string; className?: string }) {
  const Icon = PLATFORM_ICON_MAP[platform]
  if (Icon) return <Icon className={className} />
  return <span className={`inline-block shrink-0 rounded-full bg-warm-grey ${className}`} />
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-stone/30 bg-white p-5 transition-colors hover:bg-cream/40">
      <p className="text-xs font-medium uppercase tracking-wider text-warm-grey">
        {label}
      </p>
      <p className="mt-2 text-3xl font-semibold tabular-nums text-charcoal">
        {value}
      </p>
      <div className="pointer-events-none absolute -right-3 -top-3 size-16 rounded-full bg-charcoal/[0.03] transition-transform duration-300 group-hover:scale-150" />
    </div>
  )
}
